/**
 * EmailComposer - Thin wrapper over shared RichTextEditor
 * Maintains the same public interface for backward compatibility
 *
 * NOTE (2026-07-12, RichTextEditor image upload rework): the shared
 * RichTextEditor's `onImageUpload` prop changed contract to
 * `(file: File) => Promise<string>` (single file, resolves with the URL to
 * embed inline) to support real image upload + resize. This module's own
 * `onImageUpload?: (files: FileList) => void` (multi-file, fire-and-forget)
 * predates that and is NOT the same shape, so it can no longer be forwarded
 * to the shared editor's `onImageUpload`. In practice `onImageUpload` here
 * has only ever been wired to the same handler as `onFileAttach`
 * (`useEmailCompose.handleFileUpload`, which uploads files as email
 * attachments — it never embedded images inline), so the "image" toolbar
 * button is disabled here (`image: false`) and its legacy handler is folded
 * into `onFileAttach` as a fallback. No functionality is lost: attaching an
 * image via the paperclip button already does exactly what the old "image"
 * button did.
 */

import React from 'react';
import { RichTextEditor } from '@/shared/components/ui/RichTextEditor';

interface EmailComposerProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  showToolbar?: boolean;
  onImageUpload?: (files: FileList) => void;
  onFileAttach?: (files: FileList) => void;
}

export const EmailComposer: React.FC<EmailComposerProps> = ({
  value,
  onChange,
  placeholder = 'Escreva seu email...',
  minHeight = 200,
  showToolbar = true,
  onImageUpload,
  onFileAttach,
}) => {
  return (
    <RichTextEditor
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      minHeight={minHeight}
      showToolbar={showToolbar}
      features={{ attachment: true, heading: false, image: false }}
      onFileAttach={onFileAttach ?? onImageUpload}
    />
  );
};

EmailComposer.displayName = 'EmailComposer';
